/* ============================================================
   Collection toolbar + compare — Memphis-style behaviors.
   - Show/Hide Filters (desktop)
   - Grid/List view toggle (persisted per-session in localStorage)
   - Compare: select up to 4 products, sticky tray, side-by-side modal
   ============================================================ */
(function () {
  'use strict';

  if (typeof document === 'undefined') return;

  var STORAGE_KEY_VIEW    = 'dcp:collection:view';        // 'grid' | 'list'
  var STORAGE_KEY_FILTERS = 'dcp:collection:filtersOpen'; // '1' | '0'
  var STORAGE_KEY_COMPARE = 'dcp:compare:items';          // JSON array
  var COMPARE_LIMIT       = 4;

  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* ---------------- Show / Hide Filters ---------------- */

  function initFilterToggle() {
    var btn = document.querySelector('[data-filters-toggle]');
    var container = document.getElementById('CollectionProductsContainer');
    if (!btn || !container) return;

    var labelEl = btn.querySelector('.cf-filters-toggle__label');
    var labelShow = labelEl ? labelEl.dataset.labelShow : 'Show Filters';
    var labelHide = labelEl ? labelEl.dataset.labelHide : 'Hide Filters';

    // Selectors that might match the active filter sidebar across vendors:
    // theme native, Globo Smart Product Filter (spf-/gf-), Searchanise (snize-),
    // Boost PFS. Excludes drawer canvases, popups, search inputs, toggle triggers.
    var SIDEBAR_SELECTORS = [
      '.yv-filter-sidebar',
      'aside#filterSideBar',
      // Globo Smart Product Filter — confirmed via globo.filter.min.js inspection.
      // The sidebar is rendered as `<div id="gf-tree">` (ID, not class).
      // `#gf-products` is the products column, `#gf-controls-container` is the
      // toolbar (sort/search) — both must STAY visible when collapsing.
      '#gf-tree',
      '.gf-tree',
      '#gf-offcanvas',
      '.gf-offcanvas',
      // Belt-and-suspenders for any future Globo class variants
      '[id^="gf-tree"]',
      '[class*="gf-tree"]',
      // Searchanise
      '.snize-collection-filters-container',
      '.snize-collection-filters',
      '.snize-search-results-filters-container',
      '.snize-search-results-filters',
      '.snize-search-results-filter-section',
      '.snize-filter-section',
      '.snize-filter-tree',
      '.snize-sidebar',
      '[class*="snize-collection-filter"]',
      '[class*="snize-search-results-filter"]',
      '[data-snize-filter-tree]',
      // Boost PFS
      '.boost-pfs-filter-tree',
      '[class*="boost-pfs-filter-tree"]'
    ];

    function findSidebars() {
      var found = [];

      // 1. Explicit selectors (covers known apps)
      SIDEBAR_SELECTORS.forEach(function (sel) {
        try {
          document.querySelectorAll(sel).forEach(function (el) {
            if (el.closest('header, .yv-header, .header, [data-header]')) return;
            if (el.matches('[data-filters-toggle], .cf-filters-toggle')) return;
            if (found.indexOf(el) === -1) found.push(el);
          });
        } catch (e) {}
      });

      // 2. Sibling-of-products-list fallback: hide every direct sibling of
      // the products grid inside `#CollectionProductsContainer`. The theme's
      // aside#filterSideBar (and most filter apps that inject inside this
      // container) sit here.
      var list = container.querySelector('.yv-products-list');
      if (list && list.parentElement) {
        Array.prototype.slice.call(list.parentElement.children).forEach(function (sib) {
          if (sib === list) return;
          if (sib.matches('script, style, noscript, link, meta')) return;
          if (sib.matches('[data-filters-toggle], .cf-filters-toggle')) return;
          if (sib.contains(list)) return;
          if (sib.closest('header, .yv-header, .header, [data-header], footer, .footer')) return;
          if (sib.querySelector && sib.querySelector('[data-filters-toggle]')) return;
          if (found.indexOf(sib) === -1) found.push(sib);
        });
      }

      return found;
    }

    var saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY_FILTERS); } catch (e) {}
    if (saved === '0') applyState(false);
    else applyState(true);

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      var isOpen = !container.classList.contains('cf-filters-collapsed');
      applyState(!isOpen);
      try { localStorage.setItem(STORAGE_KEY_FILTERS, !isOpen ? '1' : '0'); } catch (e) {}
    });

    // Re-apply state after AJAX filter updates (DOM may swap out sidebar nodes)
    if ('MutationObserver' in window) {
      var debounce = null;
      new MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(function () {
          var open = !container.classList.contains('cf-filters-collapsed');
          applyState(open);
        }, 80);
      }).observe(document.body, { childList: true, subtree: true });
    }

    function applyState(open) {
      var isDesktop = window.matchMedia('(min-width: 992px)').matches;

      // On mobile, the Hide/Show pattern doesn't apply — the theme's
      // funnel button opens Globo's drawer instead. Always restore any
      // previously-set inline hides and skip the rest, so a saved
      // "collapsed" state from desktop doesn't break the mobile drawer.
      if (!isDesktop) {
        container.classList.remove('cf-filters-collapsed');
        document.body.classList.remove('cf-filters-collapsed');
        findSidebars().forEach(function (el) {
          if (el.dataset.cfPrevDisplay !== undefined) {
            el.style.display = el.dataset.cfPrevDisplay;
            delete el.dataset.cfPrevDisplay;
          } else if (el.style.display === 'none') {
            el.style.display = '';
          }
        });
        var listM = container.querySelector('.yv-products-list');
        if (listM) {
          listM.style.flex = '';
          listM.style.maxWidth = '';
          listM.style.width = '';
          listM.style.paddingLeft = '';
        }
        return;
      }

      container.classList.toggle('cf-filters-collapsed', !open);
      document.body.classList.toggle('cf-filters-collapsed', !open);
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (labelEl) labelEl.textContent = open ? labelHide : labelShow;

      // Brute-force: directly toggle inline display on every plausible sidebar.
      // CSS selector-guessing has proven unreliable across vendors, so we set
      // style directly on the elements we find.
      var sidebars = findSidebars();
      sidebars.forEach(function (el) {
        if (open) {
          if (el.dataset.cfPrevDisplay !== undefined) {
            el.style.display = el.dataset.cfPrevDisplay;
            delete el.dataset.cfPrevDisplay;
          } else {
            el.style.display = '';
          }
        } else {
          if (el.dataset.cfPrevDisplay === undefined) {
            el.dataset.cfPrevDisplay = el.style.display || '';
          }
          el.style.display = 'none';
        }
      });

      // Expand products list to fill the row when filters hidden
      var list = container.querySelector('.yv-products-list');
      if (list) {
        if (open) {
          list.style.flex = '';
          list.style.maxWidth = '';
          list.style.width = '';
          list.style.paddingLeft = '';
        } else {
          list.style.flex = '1 1 100%';
          list.style.maxWidth = '100%';
          list.style.width = '100%';
          list.style.paddingLeft = '0';
        }
      }
    }

    // Re-evaluate on viewport resize across the desktop/mobile boundary
    // so a saved collapsed state stops affecting things when rotating
    // a tablet or resizing the browser.
    if (window.matchMedia) {
      window.matchMedia('(min-width: 992px)').addEventListener('change', function () {
        var open = !container.classList.contains('cf-filters-collapsed');
        applyState(open);
      });
    }
  }

  /* ---------------- Grid / List view toggle ---------------- */

  function initViewToggle() {
    var buttons = document.querySelectorAll('[data-view-mode]');
    var container = document.getElementById('CollectionProductsContainer');
    if (!buttons.length || !container) return;

    var saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY_VIEW); } catch (e) {}
    apply(saved === 'list' ? 'list' : 'grid');

    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var mode = btn.getAttribute('data-view-mode');
        apply(mode);
        try { localStorage.setItem(STORAGE_KEY_VIEW, mode); } catch (e) {}
      });
    });

    function apply(mode) {
      container.classList.toggle('cf-view-list', mode === 'list');
      container.classList.toggle('cf-view-grid', mode !== 'list');
      buttons.forEach(function (b) {
        var isActive = b.getAttribute('data-view-mode') === mode;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }
  }

  /* ---------------- Compare ---------------- */

  function loadCompare() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY_COMPARE);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.slice(0, COMPARE_LIMIT) : [];
    } catch (e) { return []; }
  }

  function saveCompare(items) {
    try { localStorage.setItem(STORAGE_KEY_COMPARE, JSON.stringify(items)); } catch (e) {}
  }

  function readProductFromCard(card) {
    return {
      id:        card.getAttribute('data-compare-id'),
      handle:    card.getAttribute('data-compare-handle'),
      title:     card.getAttribute('data-compare-title') || '',
      vendor:    card.getAttribute('data-compare-vendor') || '',
      url:       card.getAttribute('data-compare-url')   || '#',
      image:     card.getAttribute('data-compare-image') || '',
      price:     card.getAttribute('data-compare-price') || '',
      compareAt: card.getAttribute('data-compare-compare-at') || '',
      available: (card.getAttribute('data-compare-available') || 'true') === 'true',
      type:      card.getAttribute('data-compare-type') || ''
    };
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function initCompare() {
    var root      = document.querySelector('[data-compare-root]');
    if (!root) return;

    var countEl   = root.querySelector('[data-compare-count]');
    var limitEl   = root.querySelector('[data-compare-limit]');
    var itemsEl   = root.querySelector('[data-compare-items]');
    var clearEl   = root.querySelector('[data-compare-clear]');
    var openEl    = root.querySelector('[data-compare-open]');
    var modal     = root.querySelector('[data-compare-modal]');
    var modalBody = root.querySelector('[data-compare-modal-body]');
    var modalClose = root.querySelectorAll('[data-compare-modal-close]');

    if (limitEl) limitEl.textContent = '/ ' + COMPARE_LIMIT;

    var state = loadCompare();
    render();
    bindCardToggles(document);

    // Re-bind after AJAX filter updates (theme's filters.js replaces .yv-collection-products contents)
    var productGrid = document.getElementById('CollectionProductsContainer');
    if (productGrid && 'MutationObserver' in window) {
      var debounce = null;
      new MutationObserver(function () {
        clearTimeout(debounce);
        debounce = setTimeout(function () { bindCardToggles(productGrid); render(); }, 50);
      }).observe(productGrid, { childList: true, subtree: true });
    }

    if (clearEl) {
      clearEl.addEventListener('click', function () {
        state = [];
        saveCompare(state);
        render();
      });
    }

    if (openEl) {
      openEl.addEventListener('click', function () {
        if (state.length < 2) return;
        renderModal();
        showModal(true);
      });
    }

    modalClose.forEach(function (el) {
      el.addEventListener('click', function () { showModal(false); });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.getAttribute('aria-hidden') === 'false') {
        showModal(false);
      }
    });

    function bindCardToggles(scope) {
      var toggles = scope.querySelectorAll('[data-compare-toggle]');
      toggles.forEach(function (btn) {
        if (btn._cfBound) {
          syncToggle(btn);
          return;
        }
        btn._cfBound = true;
        btn.addEventListener('click', function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
          var card = btn.closest('[data-compare-id]');
          if (!card) return;
          var product = readProductFromCard(card);
          var idx = state.findIndex(function (p) { return p.id === product.id; });
          if (idx > -1) {
            state.splice(idx, 1);
          } else {
            if (state.length >= COMPARE_LIMIT) {
              btn.classList.add('cf-compare-toggle--limit');
              setTimeout(function () { btn.classList.remove('cf-compare-toggle--limit'); }, 600);
              return;
            }
            state.push(product);
          }
          saveCompare(state);
          render();
        });
        syncToggle(btn);
      });
    }

    function syncToggle(btn) {
      var card = btn.closest('[data-compare-id]');
      if (!card) return;
      var id = card.getAttribute('data-compare-id');
      var on = state.some(function (p) { return p.id === id; });
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var labelEl = btn.querySelector('.cf-compare-toggle__label');
      if (labelEl) labelEl.textContent = on ? 'Added' : 'Compare';
    }

    function render() {
      root.hidden = state.length === 0;
      root.classList.toggle('is-active', state.length > 0);
      document.body.classList.toggle('cf-compare-active', state.length > 0);
      if (countEl) countEl.textContent = String(state.length);
      if (openEl)  openEl.disabled = state.length < 2;

      if (itemsEl) {
        itemsEl.innerHTML = state.map(function (p) {
          return '' +
            '<li class="cf-compare-tray__item" data-compare-tray-item="' + escapeHtml(p.id) + '">' +
              (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
              '<button type="button" class="cf-compare-tray__item-remove" aria-label="Remove ' + escapeHtml(p.title) + '">×</button>' +
            '</li>';
        }).join('');
        // Placeholders for empty slots — visual cue mirroring Memphis tray
        var placeholders = Math.max(0, COMPARE_LIMIT - state.length);
        for (var i = 0; i < placeholders; i++) {
          var li = document.createElement('li');
          li.className = 'cf-compare-tray__item cf-compare-tray__item--placeholder';
          itemsEl.appendChild(li);
        }
        itemsEl.querySelectorAll('.cf-compare-tray__item-remove').forEach(function (rm) {
          rm.addEventListener('click', function () {
            var item = rm.closest('[data-compare-tray-item]');
            if (!item) return;
            var id = item.getAttribute('data-compare-tray-item');
            state = state.filter(function (p) { return p.id !== id; });
            saveCompare(state);
            render();
          });
        });
      }

      // Sync card buttons across the grid
      document.querySelectorAll('[data-compare-toggle]').forEach(syncToggle);
    }

    function rowImages(items) {
      return items.map(function (p) {
        return '<div class="cf-compare-cell cf-compare-cell--image">' +
          (p.image ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '">' : '') +
        '</div>';
      }).join('');
    }
    function rowText(items, key, classes, isHtml) {
      return items.map(function (p) {
        var v = p[key];
        return '<div class="cf-compare-cell ' + (classes || '') + '">' + (isHtml ? (v || '') : escapeHtml(v || '—')) + '</div>';
      }).join('');
    }
    function rowPrice(items) {
      return items.map(function (p) {
        var compareAt = p.compareAt ? '<span class="cf-compare-compareat">' + escapeHtml(p.compareAt) + '</span>' : '';
        return '<div class="cf-compare-cell cf-compare-cell--price">' + escapeHtml(p.price || '—') + compareAt + '</div>';
      }).join('');
    }
    function rowAvail(items) {
      return items.map(function (p) {
        return '<div class="cf-compare-cell">' + (p.available ? 'In stock' : 'Out of stock') + '</div>';
      }).join('');
    }
    function rowTitle(items) {
      return items.map(function (p) {
        return '<div class="cf-compare-cell cf-compare-cell--title"><a href="' + escapeHtml(p.url) + '">' + escapeHtml(p.title) + '</a></div>';
      }).join('');
    }
    function rowActions(items) {
      return items.map(function (p) {
        return '<div class="cf-compare-cell cf-compare-cell--actions"><a class="button small-btn" href="' + escapeHtml(p.url) + '">View</a></div>';
      }).join('');
    }
    function rowRemove(items) {
      return items.map(function (p) {
        return '<div class="cf-compare-cell cf-compare-cell--remove"><button type="button" data-compare-modal-remove="' + escapeHtml(p.id) + '">Remove</button></div>';
      }).join('');
    }

    function renderModal() {
      if (!modalBody) return;
      if (state.length === 0) {
        modalBody.innerHTML = '<div class="cf-compare-empty">Select two or more products to compare.</div>';
        return;
      }
      var cols = state.length;
      var html = '<div class="cf-compare-grid" style="--cf-cols:' + cols + '">' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">&nbsp;</div>' +
          rowImages(state) +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">Brand</div>' +
          rowText(state, 'vendor') +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">Product</div>' +
          rowTitle(state) +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">Price</div>' +
          rowPrice(state) +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">Type</div>' +
          rowText(state, 'type') +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">Availability</div>' +
          rowAvail(state) +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">&nbsp;</div>' +
          rowActions(state) +
        '</div>' +
        '<div class="cf-compare-row">' +
          '<div class="cf-compare-cell cf-compare-cell--label">&nbsp;</div>' +
          rowRemove(state) +
        '</div>' +
      '</div>';
      modalBody.innerHTML = html;

      modalBody.querySelectorAll('[data-compare-modal-remove]').forEach(function (b) {
        b.addEventListener('click', function () {
          var id = b.getAttribute('data-compare-modal-remove');
          state = state.filter(function (p) { return p.id !== id; });
          saveCompare(state);
          render();
          if (state.length === 0) showModal(false);
          else renderModal();
        });
      });
    }

    function showModal(open) {
      if (!modal) return;
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.style.overflow = open ? 'hidden' : '';
      if (open) {
        var first = modal.querySelector('button, a');
        if (first) try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
      }
    }
  }

  /* ---------------- Boot ---------------- */
  ready(function () {
    initFilterToggle();
    initViewToggle();
    initCompare();
  });
})();
